import type { UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import InfiniteClient from "./infinite-client";
import type { WithConfig } from "./resource-client";
import { CONFIG_SYMBOL } from "./resource-client";
import { useRevertableQuery } from "./use-revertable-query";
import type { IQuery } from "./utils";

const DEFAULT_BATCHES = 4;
const DEFAULT_PAGE_SIZE = 128;

const flatten = <Model>(results: Model[][]): Model[] =>
  ([] as Model[]).concat(...results);

export type UseFindAllBatchedOptions<
  Model,
  QueryError extends Error = Error,
> = Omit<UseQueryOptions<Model[], QueryError>, "queryKey" | "queryFn"> & {
  pageSize?: number;
  batches?: number;
};

export default abstract class PageClient<
  Model,
  Identifier = string,
  QueryParams extends Record<string, any> = IQuery,
> extends InfiniteClient<Model, Identifier, QueryParams> {
  /**
   * Find all using batched, parallel pages
   *
   * This method will request several pages simultaneously (default is 4).
   *
   * If any individual page request times out, we split it in half and make
   * two requests, then stitch them back together.
   *
   * If the last page of a batch returns the same number of results as the page
   * size, we request another batch.
   *
   * At the end, we should have the complete array of results.
   *
   * @param query parameters to filter the request
   * @param pageSize number of results per page
   * @param batches number of pages to request at a time
   * @returns an array of Model
   */
  public findAllBatched(
    query: QueryParams,
    pageSize: number = DEFAULT_PAGE_SIZE,
    batches: number = DEFAULT_BATCHES,
  ): Promise<Model[]> {
    return findAllBatched(
      (options) => this.find({ ...query, ...options }),
      pageSize,
      batches,
    );
  }

  /**
   * This operates the same as useFind but it will return all results that are
   * found in an eager load
   *
   * @param {QueryParams} query
   * @param {UseQueryOptions<Model[]>} options
   * @returns {UseQueryResult<Model[]>}
   */
  useFindAllBatched(
    query: QueryParams,
    {
      batches,
      pageSize,
      ...options
    }: UseFindAllBatchedOptions<Model, AxiosError> = {},
  ): UseQueryResult<Model[], AxiosError> {
    return useRevertableQuery<Model[], AxiosError>({
      ...options,
      queryKey: [...this.getQueryCacheKey(query), "all"],
      queryFn: () => this.findAllBatched(query, pageSize, batches),
      enabled: Boolean((options.enabled ?? true) && this.isEnabled()),
    });
  }

  /**
   * for paging/infinite queries, extract the next page from the previous payload
   * @param {Object} previousPayload
   * @returns {IQuery}
   */
  protected getNextQuery(previousResult: WithConfig<Model[]>): IQuery {
    const page = (Number(previousResult[CONFIG_SYMBOL].params.page) || 1) + 1;
    return { page };
  }
}

/**
 * Find all using batched, parallel pages
 *
 * This method will request several pages simultaneously (default is 4).
 *
 * If any individual page request times out, we split it in half and make
 * two requests, then stitch them back together.
 *
 * If the last page of a batch returns the same number of results as the page
 * size, we request another batch.
 *
 * At the end, we should have the complete array of results.
 *
 * @param query parameters to filter the request
 * @param pageSize number of results per page
 * @param batches number of pages to request at a time
 * @returns an array of Model
 */
export function findAllBatched<Model>(
  find: ({
    page,
    per_page,
  }: {
    page: number;
    per_page: number;
  }) => Promise<Model[]>,
  pageSize: number = DEFAULT_PAGE_SIZE,
  batches: number = DEFAULT_BATCHES,
): Promise<Model[]> {
  const _findPage = (page: number, per_page: number): Promise<Model[]> =>
    find({
      page,
      per_page,
    });

  /**
   * Note: pages are 1-indexed because that's how our REST API works
   * batches are 0-indexed because the math is easier
   */
  const _findBatch = (batch: number = 0): Promise<Model[]> => {
    // request the first n-1 pages
    const pageFetches = Array.from({ length: batches - 1 }).map((_, index) =>
      _findPage(batches * batch + index + 1, pageSize),
    );

    // for the nth page, if it returns batchSize number of results, request another batch
    pageFetches.push(
      _findPage(batches * (batch + 1), pageSize).then((results: Model[]) => {
        if (results.length === pageSize) {
          return _findBatch(batch + 1).then((nextResults) =>
            results.concat(nextResults),
          );
        } else {
          return results;
        }
      }),
    );

    return Promise.all(pageFetches).then(flatten);
  };

  return _findBatch();
}
